// The royal court on the Diplomacy tab: your nobles with their faces and where
// they are, and a "propose marriage" action for each ally. With generations
// armed, each face wears its life stage and houses in mourning are named.
import type { PlayerHudEntry } from '@aod/ui';
import { SceneImage } from '@aod/ui/components/ui/scene';
import { useMemo } from 'react';
import {
  type Dynasty,
  type Royal,
  courtOf,
  mourningHouses,
  standingWith,
} from '../../dynasty/dynasty.js';
import { type MessageKey, useT } from '../../i18n/index.js';
import { RoyalPortrait } from '../RoyalPortrait.js';
import { courtHeadline } from '../sceneEvents.js';
import { sceneUrls } from '../scenes.js';

interface RoyalCourtProps {
  readonly dynasty: Dynasty;
  readonly localPlayerIndex: number;
  readonly players: readonly PlayerHudEntry[];
  /** Offer a marriage to an allied seat. */
  readonly onPropose?: (toPlayer: number) => void;
}

export function RoyalCourt({ dynasty, localPlayerIndex, players, onPropose }: RoyalCourtProps) {
  const t = useT();
  const nameOf = (seat: number) => players.find((p) => p.playerId === seat)?.name ?? `#${seat + 1}`;
  const status = (r: Royal): string => {
    if (r.spouseOf !== null) {
      const spouse = dynasty.royals.find((x) => x.id === r.spouseOf);
      return t('dynasty.wed', { spouse: nameOf(spouse?.seat ?? -1) }) as string;
    }
    switch (r.whereabouts.kind) {
      case 'travelling':
        return t('dynasty.travelling', { house: nameOf(r.whereabouts.to) }) as string;
      case 'held':
        return t('dynasty.held', { captor: nameOf(r.whereabouts.captor) }) as string;
      case 'dead':
        return t('dynasty.dead') as string;
      default:
        return t('dynasty.home') as string;
    }
  };
  const allies = players.filter(
    (p) => standingWith(dynasty, localPlayerIndex, p.playerId) !== 'not_allied',
  );
  const court = courtOf(dynasty, localPlayerIndex);
  // The mourning seats drive the coronation scene: the eldest living member
  // of a mourning house is its new head (generations design §6).
  const mourningSeats = useMemo(
    () => new Set(mourningHouses(dynasty).map((m) => m.seat)),
    [dynasty],
  );
  const headline = courtHeadline(court, dynasty.captures, mourningSeats);
  const rank = (r: Royal) => t(r.female ? 'dynasty.princess' : 'dynasty.prince') as string;
  return (
    <div className="flex flex-col gap-2" data-testid="royal-court">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {t('dynasty.court')}
      </h3>
      {headline && (
        <SceneImage
          {...sceneUrls(headline.scene.group, headline.scene.id as never)}
          widths={[384, 768]}
          sizes="280px"
          aspect="wide"
          caption={`${rank(headline.royal)} — ${status(headline.royal)}`}
          data-testid="court-scene"
          data-scene={headline.scene.id}
        />
      )}
      <div className="flex gap-2">
        {court.map((r) => (
          <div key={r.id} className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center">
            <RoyalPortrait
              portrait={r.portrait}
              label={rank(r)}
              stage={r.stage}
              className="h-24 w-[4.8rem]"
            />
            <span className="text-xs font-medium text-foreground">{rank(r)}</span>
            {r.stage && (
              <span
                className="rounded border border-amber-400/30 px-1 text-[9px] uppercase tracking-wide text-amber-700 dark:text-amber-300"
                data-testid={`royal-stage-${r.stage}`}
              >
                {t(`dynasty.stage.${r.stage}` as MessageKey)}
              </span>
            )}
            <span className="text-[10px] leading-tight text-muted-foreground">{status(r)}</span>
          </div>
        ))}
      </div>
      {mourningHouses(dynasty).map(({ seat }) => (
        <p
          key={seat}
          className="rounded border border-border/60 bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground"
          data-testid="royal-mourning"
        >
          {t('dynasty.mourning', { house: nameOf(seat) })}
        </p>
      ))}
      {allies.length === 0 && (
        <p className="text-[10px] text-muted-foreground">{t('dynasty.needAlliance')}</p>
      )}
      <div className="flex flex-col gap-1">
        {players
          .filter((p) => p.playerId !== localPlayerIndex)
          .map((p) => {
            const standing = standingWith(dynasty, localPlayerIndex, p.playerId);
            if (standing === 'not_allied') return null;
            return (
              <div key={p.playerId} className="flex items-center gap-2 text-xs">
                <span className="min-w-0 flex-1 truncate">{p.name}</span>
                {(standing === 'open' || standing === 'pact') && onPropose ? (
                  <>
                    {/* A pact is proposable across, but it is not an
                     *  alliance yet — say which one it is. */}
                    {standing === 'pact' && (
                      <span className="text-[10px] text-muted-foreground">
                        {t('dynasty.atPact')}
                      </span>
                    )}
                    <button
                      type="button"
                      className="rounded border border-amber-300/50 px-2 py-0.5 text-amber-100 hover:bg-amber-300/10"
                      onClick={() => onPropose(p.playerId)}
                    >
                      {t('dynasty.propose')}
                    </button>
                  </>
                ) : (
                  <span className="text-[10px] text-muted-foreground">
                    {standing === 'bonded'
                      ? t('dynasty.bonded')
                      : standing === 'incoming'
                        ? t('dynasty.incoming')
                        : standing === 'pact'
                          ? t('dynasty.atPact')
                          : t('dynasty.pending')}
                  </span>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
